import { useEffect, useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'gonka.installPrompt.dismissed';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number.parseInt(raw, 10);
    return Number.isFinite(at) && Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode / quota — fine to silently swallow */
  }
}

export function InstallPromptBanner(): ReactNode {
  const { t } = useTranslation();
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show inside Telegram WebView — Telegram hosts the wallet
    // already and its own UI provides the equivalent "open in app" path.
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) return;
    // Already installed as standalone PWA — nothing to offer.
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (isRecentlyDismissed()) return;

    const handler = (e: Event): void => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler as EventListener);
    return (): void =>
      window.removeEventListener('beforeinstallprompt', handler as EventListener);
  }, []);

  async function onInstall(): Promise<void> {
    if (!evt) return;
    setVisible(false);
    try {
      await evt.prompt();
      await evt.userChoice;
    } finally {
      setEvt(null);
      // Record either outcome — we don't pester the user again for 30 days.
      markDismissed();
    }
  }

  function onDismiss(): void {
    setVisible(false);
    setEvt(null);
    markDismissed();
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="install-banner"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
          role="dialog"
          aria-label={t('installPrompt.title')}
          // Hidden below md so the native browser install affordance
          // (Chrome address-bar icon) handles mobile and small tablets.
          className="fixed left-1/2 top-4 z-50 hidden -translate-x-1/2 items-center gap-3 rounded-xl border border-border-hairline bg-surface-1 px-4 py-3 shadow-lg md:flex"
          style={{ maxWidth: 'calc(100vw - 32px)' }}
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-text-primary">
              {t('installPrompt.title')}
            </span>
            <span className="text-[11px] text-text-secondary">
              {t('installPrompt.subtitle')}
            </span>
          </div>
          <button
            type="button"
            onClick={onInstall}
            className="rounded-lg bg-link px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            {t('installPrompt.cta')}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('common.close')}
            className="flex h-7 w-7 items-center justify-center rounded-full text-text-secondary hover:bg-surface-2 hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
